import type { Common } from '../common.js';
import {
  type AgentPermissionGroupRow,
  type OrganizationPermissionGroupRow,
  PermissionEntityType,
  type PermissionGroupRow,
  type PermissionRole,
  type UserDirectPermissions,
} from '../models/flow/permissions.js';

export class API {
  common: Common;
  baseEndpoint = '/flow/permissions';

  constructor(common: Common) {
    this.common = common;
  }

  async findAll(args: {
    entityType: PermissionEntityType.Organization;
    entityId?: string;
    organizationId?: string;
  }): Promise<OrganizationPermissionGroupRow[]>;
  async findAll(args: {
    entityType: PermissionEntityType.Agent;
    entityId?: string;
    organizationId?: string;
  }): Promise<AgentPermissionGroupRow[]>;
  async findAll(args: {
    entityType: PermissionEntityType;
    entityId?: string;
    organizationId?: string;
  }): Promise<PermissionGroupRow[]>;
  async findAll(args: {
    entityType: PermissionEntityType;
    entityId?: string;
    organizationId?: string;
  }): Promise<PermissionGroupRow[]> {
    const url = this.common.constructPath(this.baseEndpoint, args);
    const response = await this.common.getJSON(url);
    return response.permissions;
  }

  async findAllForAgents(
    args: {
      entityId?: string;
      organizationId?: string;
    } = {}
  ): Promise<AgentPermissionGroupRow[]> {
    return await this.findAll({
      ...args,
      entityType: PermissionEntityType.Agent,
    });
  }

  async findAllForOrganizations(
    args: {
      entityId?: string;
      organizationId?: string;
    } = {}
  ): Promise<OrganizationPermissionGroupRow[]> {
    return await this.findAll({
      ...args,
      entityType: PermissionEntityType.Organization,
    });
  }

  async updateRoleMemberships(args: {
    entityType: string;
    entityId: string;
    /** Map of userId to roleId (or null to remove) */
    userRoles: Record<string, string | null>;
  }): Promise<void> {
    const { entityType, entityId, userRoles } = args;
    const url = this.common.constructPath(`${this.baseEndpoint}/bulk`);
    await this.common.postJSON(url, {
      entityType,
      entityId,
      userRoles,
    });
  }

  async fetchRoles(): Promise<PermissionRole[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/roles`);
    const res = await this.common.getJSON(url);
    return res.roles;
  }

  async fetchUserDirectPermissions(): Promise<UserDirectPermissions> {
    const url = this.common.constructPath(`${this.baseEndpoint}/user`);
    const res = await this.common.getJSON(url);
    return res.permissions;
  }
}
